import type { ReactNode } from '@tanstack/react-router';
import { ModalContext } from 'engine/modalContext';
import { useEffect, useRef, useState } from 'react';

export function ModalProvider({ children }: { children: ReactNode }) {
	const [content, setContent] = useState<ReactNode>();
	const resolverRef = useRef<((v: boolean) => void) | undefined>(undefined);
	const dialogRef = useRef<HTMLDialogElement>(null);

	const showModal = async (content: ReactNode) => {
		setContent(content);

		if (resolverRef.current) {
			throw new Error('showModal called while modal is open');
		}

		const promise = new Promise<boolean>((resolve) => {
			resolverRef.current = resolve;
		});

		return await promise;
	};

	const cancelModal = () => {
		if (!resolverRef.current) {
			throw new Error('cancelModal called while modal is not open');
		}

		resolverRef.current(false);
		resolverRef.current = undefined;
		setContent(undefined);
	};

	const confirmModal = () => {
		if (!resolverRef.current) {
			throw new Error('confirmModal called while modal is not open');
		}

		resolverRef.current(true);
		resolverRef.current = undefined;
		setContent(undefined);
	};

	useEffect(() => {
		if (content) {
			dialogRef.current?.showModal();
		} else if (!content) {
			dialogRef.current?.close();
		}
	}, [content]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key == 'Escape') {
				e.preventDefault();
				cancelModal();
			}
		};

		window.addEventListener('keydown', handleEscape);
		return () => {
			window.removeEventListener('keydown', handleEscape);
		};
	}, []);

	return (
		<ModalContext.Provider value={{ showModal, cancelModal, confirmModal }}>
			{children}
			<dialog
				ref={dialogRef}
				data-visible={!!content}
				className="hidden size-full place-items-center bg-transparent data-[visible=true]:grid"
			>
				{content}
			</dialog>
		</ModalContext.Provider>
	);
}
